// ═══════════════════════════════════════════════════════════════════
// Tickets service client — vocabulaire commun du module.
//
// Un ticket couvre tout ce qu'une pharmacie demande en dehors d'une
// réclamation sur une commande livrée. La frontière est volontairement
// nette : dès qu'il s'agit d'un produit livré qui pose problème, c'est
// une réclamation EN-601, pas un ticket.
// ═══════════════════════════════════════════════════════════════════

export const CATEGORIES = [
  { id: 'disponibilite', label: 'Disponibilité / recherche produit', aide: "La pharmacie cherche un produit ou demande s'il est en stock" },
  { id: 'referencement', label: 'Demande de référencement', aide: 'Produit que nous ne distribuons pas encore' },
  { id: 'commande', label: 'Commande — modification, annulation, délai', aide: 'Porte sur une commande en cours, avant livraison', commande: true },
  { id: 'livraison', label: 'Livraison — horaires, adresse, accès', aide: 'Organisation de la tournée, coordonnées, transporteur' },
  { id: 'documents', label: 'Documents — facture, BL, certificat', aide: 'Envoi ou renvoi d’un document' },
  { id: 'compte', label: 'Compte & accès', aide: 'Portail de commande, identifiants, coordonnées à jour' },
  { id: 'tarifs', label: 'Tarifs & conditions', aide: 'Prix, remises, conditions commerciales' },
  { id: 'qualite', label: 'Qualité / sécurité du médicament', aide: 'Suspicion de falsification, défaut produit, rappel de lot', escalade: true },
  { id: 'autre', label: 'Autre', aide: '' },
];
export const categorieInfo = (id) => CATEGORIES.find(c => c.id === id) || null;
// Catégorie propre aux demandes reçues de l'Assistant Elixir (site de commande) :
// affichée, mais absente du choix à la saisie manuelle.
const CATEGORIES_RECUES = { reglement: 'Règlement / paiement' };
export const categorieLabel = (id) => categorieInfo(id)?.label || CATEGORIES_RECUES[id] || id || '—';

// La catégorie « qualité / sécurité » n'est pas un sujet de ticket : elle
// signale qu'une réclamation aurait dû être ouverte. Le module laisse
// saisir, puis impose la bascule avant toute clôture — un ticket ne doit
// jamais devenir l'endroit où une réclamation se perd.
export const escaladeObligatoire = (categorie) => !!categorieInfo(categorie)?.escalade;

export const CANAUX = [
  { id: 'telephone', label: 'Téléphone' },
  { id: 'mail', label: 'Mail' },
  { id: 'portail', label: 'Portail de commande' },
  { id: 'visite', label: 'Sur place' },
];
// « chatbot » : ticket créé par l'API chatbot-intake (Assistant Elixir du site de
// commande). Jamais proposé à la saisie manuelle.
export const canalLabel = (id) => CANAUX.find(c => c.id === id)?.label || (id === 'chatbot' ? 'Assistant Elixir (site de commande)' : id) || '—';

export const STATUTS = [
  { id: 'nouveau', label: 'Nouveau', color: '#C0392B', bg: '#FDECEA' },
  { id: 'en_cours', label: 'En cours', color: '#1A5276', bg: '#EAF2F8' },
  { id: 'en_attente', label: 'En attente', color: '#92710C', bg: '#FEF3CD' },
  { id: 'resolu', label: 'Résolu', color: '#1E6B3C', bg: '#EAF6EE' },
  { id: 'cloture', label: 'Clôturé', color: '#555', bg: '#EFEFEF' },
];
export const statutInfo = (id) => STATUTS.find(s => s.id === id) || STATUTS[0];
export const EN_COURS = ['nouveau', 'en_cours', 'en_attente'];

export const TYPES_MESSAGE = [
  { id: 'note', label: 'Note interne' },
  { id: 'appel_entrant', label: 'Appel reçu' },
  { id: 'appel_sortant', label: 'Appel passé' },
  { id: 'mail', label: 'Mail échangé' },
];
export const typeMessageLabel = (id) => TYPES_MESSAGE.find(t => t.id === id)?.label || 'Note';

// ─── Délai de traitement ────────────────────────────────────────────
// Alerte à 24 heures ouvrées. Un ticket ouvert vendredi à 16 h n'est
// donc pas en retard lundi matin : les week-ends ne comptent pas.
export const DELAI_ALERTE_H = 24;

export function heuresOuvrees(depuis, jusqu = new Date()) {
  let d = new Date(depuis);
  const fin = new Date(jusqu);
  if (isNaN(d) || d >= fin) return 0;
  let h = 0;
  while (d < fin) {
    const suivant = new Date(Math.min(d.getTime() + 3600000, fin.getTime()));
    const jour = d.getDay();            // 0 = dimanche, 6 = samedi
    if (jour !== 0 && jour !== 6) h += (suivant - d) / 3600000;
    d = suivant;
  }
  return h;
}

export const enRetard = (t) =>
  EN_COURS.includes(t?.statut) && heuresOuvrees(t?.created_at) > DELAI_ALERTE_H;

export function ageTexte(iso) {
  const h = Math.floor(heuresOuvrees(iso));
  if (h < 1) return "moins d'une heure";
  if (h < 24) return h + ' h ouvrées';
  return Math.floor(h / 24) + ' j ouvrés';
}

export const fmtHeure = (iso) => iso
  ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '';
